import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const BG = "#F8F9FA";
const MUTED = "#5b5e69";

/**
 * Admin · Activation Queue (PM22)
 *
 * Every new paid signup lands here. Ops calls the contractor within 2 hours,
 * sets up forwarding on his phone with him, and hits "Mark Forwarded" — that
 * flips the master activation flag AND extends the Stripe trial 30 days from
 * today so his free month effectively starts the moment forwarding is live.
 */
export default function AdminActivationsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");
  const [noteById, setNoteById] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/activation-queue").then((x) => x.data);
      setRows(r.rows || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const markCalled = async (id) => {
    setBusyId(id); setMsg("");
    try {
      await api.post(`/admin/activation-queue/${id}/mark-called`, { note: noteById[id] || "" });
      setMsg(`Marked ${id} as called.`);
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || e?.message || "Failed");
    } finally { setBusyId(null); }
  };

  const markForwarded = async (id) => {
    if (!window.confirm("Mark forwarding as ACTIVE? This starts the 30-day free-month clock.")) return;
    setBusyId(id); setMsg("");
    try {
      const r = await api.post(`/admin/activation-queue/${id}/mark-forwarded`).then(x => x.data);
      setMsg(r.ok
        ? `Forwarding activated. Trial extended 30d. Stripe: ${r.stripe?.attempted ? (r.stripe.ok ? "updated" : "failed") : "n/a"}`
        : `Failed: ${r.error}`);
      await load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || e?.message || "Failed");
    } finally { setBusyId(null); }
  };

  return (
    <div style={{ background: BG, minHeight: "100vh", padding: "30px 20px" }} data-testid="admin-activations-page">
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>NEARNOW · ADMIN</div>
        <h1 style={{ fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 3, color: NAVY, fontSize: 44, margin: "4px 0 8px" }}>
          ACTIVATION QUEUE
        </h1>
        <p style={{ color: MUTED, marginBottom: 22 }}>
          Every paid signup waits here until forwarding is live. Call within 2 hours. Their free month clock only starts when you tap <strong>Mark Forwarded</strong>.
        </p>

        {msg && <div data-testid="activation-msg" style={{ background: "#0A1628", color: "#fff", padding: "12px 16px", borderRadius: 10, marginBottom: 14 }}>{msg}</div>}

        {loading && <p>Loading…</p>}
        {!loading && rows.length === 0 && (
          <div data-testid="activations-empty" style={{ background: "#fff", border: "1px dashed #d0d3da", borderRadius: 12, padding: 40, textAlign: "center" }}>
            <h3 style={{ color: NAVY }}>Inbox zero. No pending activations.</h3>
            <p style={{ color: MUTED }}>Every paid contractor has forwarding live. Nice.</p>
          </div>
        )}

        {rows.map((r) => (
          <div key={r.id} data-testid={`activation-row-${r.id}`}
               style={{ background: "#fff", border: "1px solid #e5e7ee", borderRadius: 12, padding: 18, marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
              <div>
                <div style={{ fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 26, color: NAVY, letterSpacing: 1 }}>
                  {r.business_name || r.owner_name || "—"}
                </div>
                <div style={{ color: MUTED, fontSize: 14 }}>
                  {r.owner_name} · <strong>{r.primary_phone || "(no phone on file)"}</strong> · {r.city}, {r.state}
                </div>
                <div style={{ color: MUTED, fontSize: 12, marginTop: 4 }}>
                  Plan: <strong style={{ color: NAVY }}>{(r.plan || "—").toUpperCase()}</strong>
                  {" "}· Signed up: {r.created_at?.slice(0, 10) || "?"}
                  {r.activation_called_at && (
                    <> · Called: <span style={{ color: "#1A7A4A" }}>{r.activation_called_at.slice(0, 16).replace("T", " ")}</span></>
                  )}
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <span style={{
                  display: "inline-block", padding: "4px 10px", borderRadius: 999, fontSize: 11,
                  letterSpacing: 2, fontFamily: "Bebas Neue, Arial, sans-serif",
                  background: r.activation_status === "called" ? "#FFF5E5" : "#FDECEC",
                  color: r.activation_status === "called" ? "#8a4b00" : "#A32D2D",
                }}>
                  {(r.activation_status || "pending").toUpperCase()}
                </span>
              </div>
            </div>

            <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              <input
                placeholder="Call notes (e.g. 'left VM · try again 3pm')"
                value={noteById[r.id] || ""}
                onChange={(e) => setNoteById((s) => ({ ...s, [r.id]: e.target.value }))}
                data-testid={`note-input-${r.id}`}
                style={{ flex: 1, minWidth: 220, padding: "10px 12px", border: "1px solid #d0d3da", borderRadius: 10, fontSize: 14 }}
              />
              <button onClick={() => markCalled(r.id)} disabled={busyId === r.id}
                      data-testid={`mark-called-${r.id}`}
                      style={btnGhost}>MARK CALLED</button>
              <button onClick={() => markForwarded(r.id)} disabled={busyId === r.id}
                      data-testid={`mark-forwarded-${r.id}`}
                      style={btnGold}>MARK FORWARDED →</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const btnGhost = { background: "transparent", color: NAVY, border: `1.5px solid ${NAVY}`, padding: "10px 16px", borderRadius: 10, fontWeight: 700, letterSpacing: 1.5, fontSize: 12, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer" };
const btnGold = { background: GOLD, color: NAVY, border: "none", padding: "10px 18px", borderRadius: 10, fontWeight: 800, letterSpacing: 2, fontSize: 12, fontFamily: "Bebas Neue, Arial, sans-serif", cursor: "pointer" };
